import { isTickmillUK } from '@utils/companyName.utils'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { IntroducingBrokerValidateCode } from '@model/IntroducingBrokerValidateCode'
import { NameDto } from '@model/NameDto'

import {
  CampaignExtendedNameDto,
  CreateTradingAccountCampaignFormValues,
  OuterProps,
} from './CreateTradingAccountCampaign.types'

export const createInitialState = (props: OuterProps): CreateTradingAccountCampaignFormValues => {
  const { account, hasCampaignQuery, data, selectedPlatform, selectedCampaignId, t } = props
  const initialTradingAccount: CampaignExtendedNameDto = {
    id: '0',
    name: t('Trading Account.Regular Trading Account'),
    platformTypes: [],
  }
  const selectedCampaign = data.find((campaign) => campaign.id === selectedCampaignId)
  const defaultCampaign = data.find((campaign) => campaign.isCampaign)
  const campaignType = selectedCampaign || defaultCampaign || initialTradingAccount
  const shouldUseCampaign = hasCampaignQuery || selectedCampaignId
  const tradingAccount = shouldUseCampaign ? campaignType : initialTradingAccount
  const campaignId = tradingAccount.isCampaign ? tradingAccount.id : null
  const hasActiveMainReferral = !!account?.introducingBroker && !!props.ibValidation?.isActive
  const introducingBroker =
    hasActiveMainReferral && account?.introducingBroker ? account.introducingBroker : undefined
  const initialIbCode = hasActiveMainReferral ? account?.introducingBroker?.name || '' : ''

  const values = {
    tradingAccount,
    campaignId,
    calculationType: campaignType?.calculationType?.id
      ? { id: campaignType.calculationType.id, name: campaignType.calculationType.name }
      : undefined,
    introducingBroker,
    leverageType: undefined,
    leverageTypes: [],
    platformType: selectedPlatform,
    receivesReports: true,
    accountType: undefined,
    wallet: undefined,
    currencyId: undefined,

    ibCode: initialIbCode,
    initialIbCode,

    referralOptionId: hasActiveMainReferral
      ? account?.introducingBroker?.id || GenericReferralType.None
      : GenericReferralType.None,

    terms: false,
    newTradingAccountPassword: '',
    markup: true,
    ibState: { id: '0', name: 'initial' },
  }
  return { ...values, ibState: createIbInitialState(props, values) }
}

export const filterReferralOptions = (props: OuterProps) => (referral: NameDto<string>) =>
  !props.account?.introducingBroker ? referral.id !== GenericReferralType.Existing : referral

export enum GenericReferralType {
  Existing = '2',
  New = '1',
  None = '0',
}

export const createIbInitialState = (
  props: OuterProps,
  values?: CreateTradingAccountCampaignFormValues
) => {
  if (values?.introducingBroker && props.ibValidation?.isActive) {
    return {
      id: values.introducingBroker.id,
      name: props.t('Existing Referral'),
    }
  }

  return {
    id: GenericReferralType.None,
    name: props.t('No Referral'),
  }
}

export const createReferralOptions = (props: OuterProps) => {
  const { account, ibValidation, t } = props
  const hasMainIB = !!account?.introducingBroker
  const isMainIBInactive = hasMainIB && !ibValidation?.isActive
  const getReferralName = () =>
    isMainIBInactive
      ? `${account?.introducingBroker?.name || ''} (${t('IB was disabled')})`
      : hasMainIB
      ? t('Existing Referral with message')
      : t('Existing Referral')
  const options = [
    { id: GenericReferralType.New, name: t('New Referral') },
    {
      id: GenericReferralType.None,
      name: hasMainIB ? t('No Referral with message') : t('No Referral'),
    },
    {
      id: account?.introducingBroker?.id || GenericReferralType.Existing,
      name: getReferralName(),
      disabled: isMainIBInactive,
    },
  ]
  if (isTickmillUK(props.account)) {
    return options.filter((option) => option.id !== GenericReferralType.New)
  }
  return options
}

export const shouldHideReferral = (props: {
  hasAnyLiveTA: boolean
  ibValidation: IntroducingBrokerValidateCode | null
  account?: AccountDetailedDto
}) => !props.hasAnyLiveTA && !!props.account?.introducingBroker && props.ibValidation?.isActive
